// components/BarcodeDisplay.tsx
import React, { useEffect, useRef } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

interface BarcodeDisplayProps {
  value: string;
  width?: number;
  height?: number;
  showText?: boolean;
  format?: string;
  style?: 'table' | 'label' | 'compact';
}

const BarcodeDisplay: React.FC<BarcodeDisplayProps> = ({
  value,
  width = 120,
  height = 40,
  showText = true,
  format = 'CODE128',
  style = 'compact'
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current && window.JsBarcode) {
      try {
        // Different settings based on style
        const settings = {
          table: {
            width: 1.5,
            height: height - (showText ? 15 : 5),
            displayValue: showText,
            fontSize: 10,
            margin: 2,
            background: '#ffffff',
            lineColor: '#000000'
          },
          label: {
            width: 2,
            height: height - (showText ? 20 : 10),
            displayValue: showText,
            fontSize: 14,
            margin: 10,
            background: '#ffffff',
            lineColor: '#000000'
          },
          compact: {
            width: 1,
            height: height - (showText ? 12 : 3),
            displayValue: showText,
            fontSize: 8,
            margin: 1,
            background: '#ffffff',
            lineColor: '#000000'
          }
        };

        window.JsBarcode(canvasRef.current, value, {
          format: format,
          ...settings[style]
        });
      } catch (error) {
        console.warn('JsBarcode generation failed:', error);
        // Simple fallback barcode pattern
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);
          ctx.fillStyle = '#000000';
          
          // Create simple barcode pattern
          const barWidth = 2;
          let x = 5;
          for (let i = 0; i < value.length && x < width - 10; i++) {
            const charCode = value.charCodeAt(i);
            const barHeight = height - (showText ? 15 : 5);
            
            // Alternate thick/thin bars based on character
            if (charCode % 2 === 0) {
              ctx.fillRect(x, 3, barWidth, barHeight);
            }
            x += barWidth + 1;
            
            // Add space
            if (charCode % 3 === 0) {
              x += 2;
            }
          }
          
          // Add text if needed
          if (showText) {
            ctx.font = '8px monospace';
            ctx.textAlign = 'center';
            ctx.fillText(value, width / 2, height - 2);
          }
        }
      }
    }
  }, [value, width, height, showText, format, style]);

  return (
    <Tooltip title={`Barcode: ${value}`}>
      <Box
        sx={{
          display: 'inline-block',
          border: style === 'table' ? 1 : 0,
          borderColor: 'grey.300',
          borderRadius: style === 'table' ? 0.5 : 0,
          overflow: 'hidden',
          cursor: 'pointer',
          bgcolor: 'white',
          '&:hover': {
            borderColor: style === 'table' ? 'primary.main' : 'transparent',
            boxShadow: style === 'table' ? 1 : 0
          }
        }}
      >
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={{ display: 'block' }}
        />
      </Box>
    </Tooltip>
  );
};

// Hook for loading JsBarcode
export const useJsBarcode = () => {
  useEffect(() => {
    if (!window.JsBarcode) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jsbarcode/3.11.5/JsBarcode.all.min.js';
      script.async = true;
      document.head.appendChild(script);

      return () => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      };
    }
  }, []);
};

export default BarcodeDisplay;